import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { useTranslation } from "react-i18next";

const BoxChart = ({ data, tripRte, tripDir, inbound }) => {

  const [t] = useTranslation('common');

  const chartRef = useRef();
  const [refreshKey, setRefreshKey] = useState(0);

  let in_label = inbound;

  useEffect(() => {

    // Specify the dimensions of the chart.
    const margins = { top: 20, right: 20, bottom: 70, left: 40 };
    const width = chartRef.current.parentElement.offsetWidth;
    const height = chartRef.current.parentElement.offsetHeight;
    const innerWidth = width - margins.left - margins.right;
    const innerHeight = height - margins.top - margins.bottom;


    // 1. extract unique trip_ids in the order you want them
    const tripIds = Array.from(new Set(data.map(d => d.trip_id)));

    // 2. map trip_ids to their plannedstarttime values (the x-axis)
    const thresholds = tripIds
      .map(id => {
        const row = data.find(d => d.trip_id === id);
        return +row.plannedstarttime;
      })
      .sort((a, b) => a - b); //tresholds must be sorted

    const last = thresholds[thresholds.length - 1];

    // 3. Perform binning using plannedstarttime as the x value
    const bins = d3.bin()
      .thresholds(thresholds)
      .value(d => +d.plannedstarttime)
      (data)
      .map(bin => {
        // sort by Y value
        bin.sort((a, b) => a.realduration - b.realduration);

        const values = bin.map(d => d.realduration);

        const min = values[0];
        const max = values[values.length - 1];

        const q1 = d3.quantile(values, 0.25);
        const q2 = d3.quantile(values, 0.50);
        const q3 = d3.quantile(values, 0.75);

        const iqr = q3 - q1;
        const r0 = Math.max(min, q1 - iqr * 1.5);
        const r1 = Math.min(max, q3 + iqr * 1.5);

        const plannedValues = bin.map(d => d.plannedduration);
        bin.plannedDuration = d3.mean(plannedValues);

        const plannedStart = bin.map(d => d.plannedstarttime);
        bin.plannedStart = d3.mean(plannedStart);

        bin.quartiles = [q1, q2, q3];
        bin.range = [r0, r1];
        bin.outliers = bin.filter(v => v.realduration < r0 || v.realduration > r1);

        return bin;
      });


    //Add width to last bin
    bins[bins.length - 1].x1 = bins[bins.length - 1].x1 + bins[bins.length - 1].range[0]

    // Prepare the positional scales for x axis transformations
    const formatTime = d => {
      const date = new Date(1970, 0, 1, 0, 0, d);
      return d3.timeFormat("%H:%M:%S")(date);
    };

    const tickValues = bins.map(bin => (bin.x0 + bin.x1) / 2);
    const tickLabels = bins.map(bin => formatTime(bin.plannedStart));

    const x = d3.scaleLinear()
      .domain([d3.min(bins, d => d.x0), d3.max(bins, d => d.x1)])
      .rangeRound([margins.left, width - margins.right])


    //Prepare positional scales for y axis transformations

    const y = d3.scaleLinear()
      .domain([0, d3.max(bins, d => d.range[1])]).nice()
      .range([height - margins.bottom, margins.top])

    const formatMinutes = d => (d / 60).toFixed(0);


    // Create the SVG container.
    const chart = d3.select(chartRef.current)
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", [0, 0, width, height])
      .attr("style", "max-width: 100%; height: auto; font: 10px sans-serif;")
      .attr("text-anchor", "middle");

    //Create tooltip
    const tooltip = d3.select('.container').append('div')
      .attr('class', 'tooltipotp')
      .html('Tooltip')
      .attr('id', 'tooltipid')

    // Create a visual representation for each bin.
    const g = chart.append("g")
      .selectAll("g")
      .data(bins)
      .join("g");

    // Range.
    g.append("path")
      .attr("stroke", "currentColor")
      .attr("d", d => `
        M${x((d.x0 + d.x1) / 2)},${y(d.range[1])}
        V${y(d.range[0])}
      `);

    // Quartiles.
    //M = move to
    //H = horizontal line to
    //V = vertical line to
    //Z = close path

    const maxWidth = 40;

    g.append("path")
      .attr("fill", "#ddd")
      //Original code for reference
      // .attr("d", d => `
      //   M${x(d.x0) + 1},${y(d.quartiles[2])}
      //   H${d3.min(x(d.x1),20)}
      //   V${y(d.quartiles[0])}
      //   H${d3.min(x(d.x0) + 1,20)}
      //   Z
      // `)
      .attr("d", d => {
        const x0 = x(d.x0) + 1;
        const x1 = x(d.x1);

        const fullWidth = x1 - x0;
        const boxWidth = Math.min(fullWidth, maxWidth);

        // center the limited-width box inside the bin
        const left = x0 + (fullWidth - boxWidth) / 2;
        const right = left + boxWidth;

        return `
          M${left},${y(d.quartiles[2])}
          H${right}
          V${y(d.quartiles[0])}
          H${left}
          Z
        `;
      })
      .on('mouseover', d => {
        chart.mouse = d3.pointer(d);
        chart.mouse2 = d3.pointer(d, d3.select(chart.chartLocation));
        tooltip.html(`
          Min : ${(d.srcElement.__data__.range[0] / 60).toFixed(0)}<br/>
          1st quartile : ${(d.srcElement.__data__.quartiles[0] / 60).toFixed(0)}<br/>
          Median : ${(d.srcElement.__data__.quartiles[1] / 60).toFixed(0)}<br/>
          Planned : ${(d.srcElement.__data__.plannedDuration / 60).toFixed(0)}<br/>
          3rd quartile : ${(d.srcElement.__data__.quartiles[2] / 60).toFixed(0)}<br/>
          Max : ${(d.srcElement.__data__.range[1] / 60).toFixed(0)}<br/>
        `)
          .style('left', chart.mouse2[0] - 75 + 'px')
          .style('top', chart.mouse2[1] - tooltip.node().offsetHeight + 'px')
          .style('opacity', .9);
      }).on('mouseout', (d) => {
        tooltip.style('opacity', 0)
          .style('left', '0px');
      }).on("mouseleave", () => {
        tooltip.style('opacity', 0)
          .style('left', '0px');
      });

    // Median.
    g.append("path")
      .attr("stroke", "currentColor")
      .attr("stroke-width", 2)
      //Original code for reference
      // .attr("d", d => `
      //   M${x(d.x0) + 1},${y(d.quartiles[1])}
      //   H${x(d.x1)}
      // `)
      .attr("d", d => {
        const x0 = x(d.x0) + 1;
        const x1 = x(d.x1);

        const fullWidth = x1 - x0;
        const boxWidth = Math.min(fullWidth, maxWidth);

        // center the limited-width box inside the bin
        const left = x0 + (fullWidth - boxWidth) / 2;
        const right = left + boxWidth;
        return `
        M${left},${y(d.quartiles[1])}
        H${right}
      `
      })
      .on('mouseover', d => {
        chart.mouse = d3.pointer(d);
        chart.mouse2 = d3.pointer(d, d3.select(chart.chartLocation));
        tooltip.html(`
          Min : ${(d.srcElement.__data__.range[0] / 60).toFixed(0)}<br/>
          1st quartile : ${(d.srcElement.__data__.quartiles[0] / 60).toFixed(0)}<br/>
          Median : ${(d.srcElement.__data__.quartiles[1] / 60).toFixed(0)}<br/>
          Planned : ${(d.srcElement.__data__.plannedDuration / 60).toFixed(0)}<br/>
          3rd quartile : ${(d.srcElement.__data__.quartiles[2] / 60).toFixed(0)}<br/>
          Max : ${(d.srcElement.__data__.range[1] / 60).toFixed(0)}<br/>
        `)
          .style('left', chart.mouse2[0] - 75 + 'px')
          .style('top', chart.mouse2[1] - tooltip.node().offsetHeight + 'px')
          .style('opacity', .9);
      }).on('mouseout', (d) => {
        tooltip.style('opacity', 0)
          .style('left', '0px');
      }).on("mouseleave", () => {
        tooltip.style('opacity', 0)
          .style('left', '0px');
      });

    // Outliers, with a bit of jitter.
    g.append("g")
      .attr("fill", "currentColor")
      .attr("fill-opacity", 0.2)
      .attr("stroke", "none")
      .attr("transform", d => `translate(${x((d.x0 + d.x1) / 2)},0)`)
      .selectAll("circle")
      .data(d => d.outliers)
      .join("circle")
      .attr("r", 2)
      .attr("cx", () => (Math.random() - 0.5) * 4)
      .attr("cy", d => y(d.realduration));

    //Planned durations
    chart.selectAll(".planned-dot")
      .data(bins)
      .enter()
      .append("circle")
      .attr("class", "planned-dot")
      // X = center of the bin
      .attr("cx", d => x((d.x0 + d.x1) / 2))
      // Y = average planned duration
      .attr("cy", d => y(d.plannedDuration))
      .attr("r", 3)
      .style("fill", "red");

    // Append the x axis.
    chart.append("g")
      .attr("transform", `translate(0,${height - margins.bottom})`)
      .call(d3.axisBottom(x)
        .tickValues(tickValues)
        .tickFormat((d, i) => tickLabels[i])
      )
      .selectAll("text")
      .attr("transform", "rotate(-90)")
      .attr("text-anchor", "end")   // aligns text to the end of the tick
      .attr("dx", "-0.8em")         // horizontal shift
      .attr("dy", "-0.5em");


    // Append the y axis.
    chart.append("g")
      .attr("transform", `translate(${margins.left},0)`)
      .call(d3.axisLeft(y)
        .tickValues(d3.range(0, d3.max(bins, d => d.range[1]) + 1, 300))
        .tickFormat(formatMinutes))
      .call(g => g.select(".domain").remove());

    function updateGraph() {

      //Redraw graph is window is resized
      d3.select(chartRef.current).selectAll("*").remove();
      setRefreshKey(oldKey => oldKey + 1);
    }

    d3.select(window).on('resize.updatesvg', updateGraph);

  }, [data, refreshKey]);


  return <svg ref={chartRef}></svg>;
};

export default BoxChart;